import { Controller, useForm } from 'react-hook-form'

import { useDropdown } from '@/entities/dropdowns/libs/useDropdown'
import { MapField } from '@/shared/components/AddressFields/MapField'
import { SelectDropdown } from '@/shared/components/SelectDropdown/SelectDropdown'
import { TextField } from '@/shared/components/TextField/TextField'
import { pxToRem } from '@/shared/styles/themes'
import { Grid2 as Grid, styled, Typography } from '@mui/material'

import { GroupFormValues } from '../models/OnboardingFormFields'

export type GroupSetupFormProps = {
  initialValues: GroupFormValues
  onSubmit: (data: GroupFormValues) => void
  isSubmitting: boolean
}

export const GroupSetupForm: React.FC<GroupSetupFormProps> = ({
  initialValues,
  onSubmit,
  isSubmitting,
}) => {
  const { control, handleSubmit } = useForm<GroupFormValues>({
    defaultValues: initialValues,
  })

  const { data: timeZonesOptions, completed: fetchTimeZoneCompleted } = useDropdown('timeZones')

  const onFormSubmit = (data: GroupFormValues) => {
    onSubmit(data)
  }

  return (
    <StyledForm id="onboarding-step-1" onSubmit={handleSubmit(onFormSubmit)}>
      <Typography variant="h2" sx={{ fontSize: pxToRem(24), fontWeight: 500, mb: 2 }}>
        Create Your First Group
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        Groups allow you to organize your operations based on your business structure. Create your first group to get started.
      </Typography>

      <Grid container columnSpacing={2}>
        <Grid size={{ xs: 12 }}>
          <Controller
            name="name"
            control={control}
            rules={{ required: 'Group name is required' }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Group Name"
                placeholder="Enter group name"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Controller
            name="timeZone"
            control={control}
            rules={{ required: 'Time zone is required' }}
            render={({ field, fieldState }) => (
              <SelectDropdown
                {...field}
                disableClearable={true}
                label="Time Zone"
                placeholder="Select time zone"
                value={field.value}
                options={timeZonesOptions}
                onChange={(_, value) => field.onChange(value)}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                getOptionLabel={(option) => option?.label || ''}
                disabled={!fetchTimeZoneCompleted || isSubmitting}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
                required
              />
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" gutterBottom marginTop={1}>
            Group Map Center (Latitude, Longitude, Zoom)
          </Typography>
          <Controller
            name="mapCenter"
            control={control}
            render={({ field }) => (
              <MapField
                required={true}
                label="Group Map Center"
                value={field.value}
                onChange={field.onChange}
                disabled={isSubmitting}
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledForm>
  )
}

const StyledForm = styled('form')`
  width: 100%;
`